import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import {
  Alert,
  Button,
  Card,
  Conditional,
  Field,
  Select,
  Textarea,
} from '@payaca/ambrosia';
import List from '@payaca/components/list/List';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { Currency } from '@payaca/types/currencyTypes';
import { clstx } from '@payaca/utilities/cvaMerge';
import { format, isSameDay, isToday, isTomorrow } from 'date-fns';
import { isEqual } from 'lodash-es';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useGetMyRegionPreferences from '../../../../api/queries/me/useGetMyRegionPreferences';
import { humanizeCurrency } from '../../../../helpers/currency';
import MaterialCard, {
  MaterialCardProps,
} from '../../materialCard/MaterialCard';
import { FormStageStage, TFormState } from '../CreatePurchaseOrderSidebar';

const getReadableDateTime = (d: Date, locale?: string) => {
  const time = format(d, 'HH:mm');
  if (isToday(d)) {
    return `today ${time}`;
  } else if (isTomorrow(d)) {
    return `tomorrow ${time}`;
  } else {
    return format(
      d,
      getInternationalDateFormatByLocale(
        DateFormats.TIMESTAMP_DAY_MONTH,
        locale
      )
    );
  }
};

export const getReadableCallOffDate = (d: Date, locale?: string) => {
  return format(
    d,
    getInternationalDateFormatByLocale(DateFormats.DAY_MONTH_SHORT, locale)
  );
};

type MaterialsTotal = {
  exTax: { value: number; currency?: Currency };
  incTax: { value: number; currency?: Currency };
};
const getCalculateMaterialsForSupplierTotal = (
  materials: TFormState['materials'],
  supplierId: number | undefined
): MaterialsTotal | { error: string } => {
  const result: MaterialsTotal = {
    exTax: { value: 0, currency: undefined },
    incTax: { value: 0, currency: undefined },
  };
  let error;

  for (let i = 0; i < materials.length; i++) {
    const curr = materials[i];

    if (!curr.purchaseOrderQuantity) continue;

    const supplierMaterial =
      curr.materialsListMaterial.material.suppliedBy.find(
        (s) => +s.supplier.id === supplierId
      );

    if (supplierMaterial?.price.unitPriceExcTax) {
      result.exTax.value +=
        supplierMaterial.price.unitPriceExcTax.value *
        curr.purchaseOrderQuantity;

      if (
        (result.exTax.currency &&
          !isEqual(
            result.exTax.currency,
            supplierMaterial.price.unitPriceExcTax.currency
          )) ||
        (result.incTax.currency &&
          !isEqual(
            result.incTax.currency,
            supplierMaterial.price.unitPrice.currency
          ))
      ) {
        error = 'Materials have different currencies';
        break;
      } else {
        result.exTax.currency = supplierMaterial.price.unitPriceExcTax.currency;
      }
    }

    if (supplierMaterial?.price.unitPrice) {
      result.incTax.value +=
        supplierMaterial.price.unitPrice.value * curr.purchaseOrderQuantity;
      result.incTax.currency = supplierMaterial.price.unitPrice.currency;
    }
  }
  if (error) {
    console.error(error);
    return { error };
  }

  return result;
};

export interface IProps {
  formStage: FormStageStage;
  isSaving: boolean;
}
const DirectPurchaseOrderSummary: FC<IProps> = (props) => {
  const { formStage, isSaving } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const { data: accountUsers } = useGetMyAccountUsersExcludedDeactivated({
    hasAnyPermission: ['materialsList.getSelfAssignedEventDealPurchaseOrders'],
  });

  const formMethods = useFormContext<TFormState>();

  const selectedMaterials = formMethods.watch('materials');
  const selectedSupplierId = formMethods.watch('supplierId');
  const selectedCollection = formMethods.watch('collection');
  const selectedDelivery = formMethods.watch('delivery');
  const fromDate = formMethods.watch('fromDate');
  const toDate = formMethods.watch('toDate');
  const callOffDate = formMethods.watch('callOffDate');

  const selectedMaterialsTotals = useMemo(() => {
    return getCalculateMaterialsForSupplierTotal(
      selectedMaterials,
      selectedSupplierId
    );
  }, [selectedMaterials, selectedSupplierId]);

  const materialsOnOrder = selectedMaterials.filter(
    (m) => m.purchaseOrderQuantity > 0
  );

  const isCollection = !!selectedCollection?.branchId;

  const getReadableFromDate = () => {
    if (fromDate) {
      return getReadableDateTime(fromDate, regionPreferences?.locale);
    }
    if (callOffDate) {
      return getReadableCallOffDate(callOffDate, regionPreferences?.locale);
    }
    return null;
  };
  const readableFromDate = getReadableFromDate();

  const getDeliveryFromToDateTimes = (fromDate: Date, toDate: Date) => {
    const isFomAndToDateSameDay = isSameDay(fromDate, toDate);
    if (isFomAndToDateSameDay) {
      return `${readableFromDate} - ${format(toDate, 'HH:mm')}`;
    } else {
      return `${readableFromDate} - ${getReadableDateTime(
        toDate,
        regionPreferences?.locale
      )}`;
    }
  };

  const getReadableDeliveryDateTime = () => {
    if (!toDate) {
      return readableFromDate;
    }
    if (fromDate) {
      return getDeliveryFromToDateTimes(fromDate, toDate);
    }
    return null;
  };
  const readableDeliveryDateTime = getReadableDeliveryDateTime();

  const userContactOptions = (accountUsers || [])
    .filter((u) => !!u.phone && u.status === 'Active')
    .map((u) => ({
      label: `${u.fullName} - ${u.phone}`,
      value: u.id,
      metadata: u,
    }));

  return (
    <div className="z-10 overflow-auto flex flex-col h-full transition-colors duration-700 bg-gray-50 border-l">
      <div
        className={clstx(
          'p-3.5 z-10 transition-colors duration-700 sticky top-0 bg-gray-50 flex flex-col gap-2',
          scrollY > 0 && 'shadow'
        )}
      >
        <Controller
          name="userContactId"
          control={formMethods.control}
          render={({ field: { onChange, value, name } }) => {
            return (
              <Field className="mb-4" name={name}>
                <Field.Label>Contact details</Field.Label>
                <Select
                  options={userContactOptions}
                  value={value}
                  onChange={onChange}
                  CustomSelected={({ selectedOptions }) => {
                    return (
                      <span>
                        <span className="font-semibold">
                          {selectedOptions?.[0]?.metadata?.fullName}
                        </span>{' '}
                        - {selectedOptions?.[0]?.metadata?.phone}
                      </span>
                    );
                  }}
                />
              </Field>
            );
          }}
        />
        {/* cannot send additional notes for ACO delivery as we don't know the fulfilling branch */}
        {!(selectedDelivery && !selectedDelivery.branchId) && (
          <Controller
            name="additionalNotes"
            control={formMethods.control}
            render={({ field: { onChange, value, onBlur, name } }) => {
              return (
                <Field name={name} className="mb-4">
                  <Field.Label>Additional notes</Field.Label>
                  <Textarea value={value} onChange={onChange} onBlur={onBlur} />
                </Field>
              );
            }}
          />
        )}
        <h4>Order summary</h4>
        <Conditional condition={isCollection}>
          <p>
            Collect from <strong>{readableFromDate}</strong> at{' '}
            <strong>{selectedCollection?.branchName}</strong>
          </p>
        </Conditional>
        <Conditional condition={!isCollection}>
          <p>
            Expected delivery <strong>{readableDeliveryDateTime}</strong>
            {selectedDelivery?.branchName && (
              <>
                {' '}
                by <strong>{selectedDelivery?.branchName}</strong>
              </>
            )}{' '}
            to <strong>{selectedDelivery?.fullLocalAddress}</strong>
          </p>
        </Conditional>
      </div>

      {/* materials on order */}
      <List
        className="px-3.5"
        items={materialsOnOrder}
        item={(material) => {
          let materialCardProps: MaterialCardProps = {
            name: material.materialsListMaterial.material.name,
            price:
              material.materialsListMaterial.material.suppliedBy[0]?.price
                .unitPriceExcTax,
            tags: material.materialsListMaterial.material.tags,
            thumbnailUrl:
              material.materialsListMaterial.material.image?.thumbnailUrl ||
              undefined,
          };
          const suppliedBy =
            material.materialsListMaterial.material.suppliedBy.find(
              (s) => +s.supplier.id === selectedSupplierId
            );
          const unitPriceExTax = suppliedBy?.price.unitPriceExcTax;

          if (formStage === 'MATERIAL_LIST_MATERIALS') {
            materialCardProps = {
              ...materialCardProps,
              quantity: material.selectedQuantity,
              maxQuantity: material.materialQuantity,
              onChangeQuantity: (newQuantity) => {
                if (
                  newQuantity > material.materialQuantity ||
                  newQuantity < 0
                ) {
                  return;
                }

                formMethods.setValue(
                  'materials',
                  selectedMaterials.map((selectedMaterial) => {
                    return selectedMaterial.id === material.id
                      ? {
                          ...selectedMaterial,
                          selectedQuantity: newQuantity,
                          purchaseOrderQuantity: newQuantity,
                        }
                      : selectedMaterial;
                  })
                );
              },
            };
          }

          return (
            <MaterialCard
              key={material.id}
              childrenClassName={'w-full flex justify-end'}
              {...materialCardProps}
            >
              <div className="flex flex-col items-end">
                <span>Qty: {material.purchaseOrderQuantity}</span>
                <span>
                  Total (ex tax):{' '}
                  <strong>
                    {humanizeCurrency({
                      value:
                        material.purchaseOrderQuantity *
                        (unitPriceExTax?.value || 0),
                      currency: unitPriceExTax?.currency,
                    })}
                  </strong>
                </span>
              </div>
            </MaterialCard>
          );
        }}
      />

      <div className="p-3.5 z-10 transition-colors duration-700 sticky bottom-0 shadow bg-gray-50 mt-auto">
        <Card className="mb-4" sizeVariant="sm">
          <Card.Body>
            {'error' in selectedMaterialsTotals ? (
              <Alert colour="red" className="mt-4">
                Unable to get totals - {selectedMaterialsTotals.error}
              </Alert>
            ) : (
              <>
                <p className="text-right">
                  Total (ex tax):{' '}
                  <strong>
                    {humanizeCurrency({
                      value: selectedMaterialsTotals.exTax.value,
                      currency: selectedMaterialsTotals.exTax.currency,
                    })}
                  </strong>
                </p>
                <p className="text-right">
                  Total:{' '}
                  <strong>
                    {humanizeCurrency({
                      value: selectedMaterialsTotals.incTax.value,
                      currency: selectedMaterialsTotals.incTax.currency,
                    })}
                  </strong>
                </p>
              </>
            )}
          </Card.Body>
        </Card>
        <Button
          className="w-full"
          type="submit"
          disabled={isSaving || 'error' in selectedMaterialsTotals}
          isProcessing={isSaving}
        >
          Submit order
        </Button>
      </div>
    </div>
  );
};

export default DirectPurchaseOrderSummary;
